/**
 * Accessible confirmation dialog.
 *
 * Replaces `window.confirm(...)` with a styled modal so destructive
 * actions get a clear, on-brand confirmation flow.
 *
 *   const [open, setOpen] = useState(false);
 *   <ConfirmDialog
 *     open={open}
 *     title="Delete this child's record?"
 *     body="This action cannot be undone."
 *     confirmLabel="Delete"
 *     confirmSeverity="error"
 *     onConfirm={async () => { await doDelete(); }}
 *     onClose={() => setOpen(false)}
 *   />
 */
import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import { FiAlertTriangle, FiInfo, FiCheckCircle } from "react-icons/fi";

const SEVERITY_ICON = {
    error: FiAlertTriangle,
    warning: FiAlertTriangle,
    info: FiInfo,
    success: FiCheckCircle,
};

const SEVERITY_COLOR = {
    error: "error",
    warning: "warning",
    info: "info",
    success: "success",
};

export function ConfirmDialog({
    open,
    title,
    body,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    confirmSeverity = "primary",
    onConfirm,
    onClose,
    loading = false,
}) {
    const [busy, setBusy] = useState(false);
    const Icon = SEVERITY_ICON[confirmSeverity] || FiInfo;
    const isLoading = loading || busy;

    const handleConfirm = async () => {
        if (!onConfirm) {
            onClose?.();
            return;
        }
        setBusy(true);
        try {
            await onConfirm();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={isLoading ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        >
            <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{
                    px: 3,
                    pt: 3,
                    pb: 1,
                }}
            >
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        bgcolor: (t) =>
                            `${t.palette[SEVERITY_COLOR[confirmSeverity] || "info"].main}1A`,
                        color: (t) =>
                            t.palette[SEVERITY_COLOR[confirmSeverity] || "info"].main,
                    }}
                >
                    <Icon size={22} />
                </Stack>
                <DialogTitle sx={{ p: 0, fontSize: "1.1rem", fontWeight: 600 }}>
                    {title}
                </DialogTitle>
            </Stack>
            <DialogContent sx={{ pt: 1 }}>
                {typeof body === "string" ? (
                    <Typography variant="body2" color="text.secondary">
                        {body}
                    </Typography>
                ) : (
                    body
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
                <Button onClick={onClose} disabled={isLoading} color="inherit">
                    {cancelLabel}
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={isLoading}
                    color={SEVERITY_COLOR[confirmSeverity] || "primary"}
                    variant="contained"
                    startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : null}
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ConfirmDialog;
